import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, MapPin, CalendarDays, PlayCircle, MessageCircle, CalendarPlus } from "lucide-react";
import { useLang } from "@/i18n";
import Registration from "@/components/site/Registration";
import Teams from "@/components/site/Teams";
import Leaderboard from "@/components/site/Leaderboard";
import ProgrammeOfficiel from "@/components/site/ProgrammeOfficiel";
import Footer from "@/components/site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function BlurSoon({ children, testid, active = true }) {
  if (!active) return <div data-testid={testid}>{children}</div>;
  return (
    <div className="relative overflow-hidden" data-testid={testid}>
      <div className="blur-md select-none pointer-events-none opacity-70" aria-hidden="true">{children}</div>
      <div className="absolute inset-0 flex items-center justify-center bg-[#0A0A0C]/40">
        <div className="border border-[#C5A059]/60 bg-[#0A0A0C]/90 text-center px-10 py-6">
          <p className="font-display uppercase font-bold text-[#C5A059] tracking-[0.3em] text-2xl">Coming Soon</p>
        </div>
      </div>
    </div>
  );
}

export default function DetectionPage() {
  const [detections, setDetections] = useState([]);
  const [active, setActive] = useState(null);
  const [detailsVisibles, setDetailsVisibles] = useState(false);
  const [whatsapp, setWhatsapp] = useState("+243862624007");
  const { t } = useLang();

  useEffect(() => {
    axios.get(`${API}/detections`).then((r) => {
      setDetections(r.data);
      if (r.data.length > 0) setActive(r.data[0].id);
    }).catch(() => {});
    axios.get(`${API}/settings`).then((r) => {
      setDetailsVisibles(!!r.data.details_visibles);
      if (r.data.whatsapp) setWhatsapp(r.data.whatsapp);
    }).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const waUrl = `https://wa.me/${whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(t("det.wa.msg"))}`;

  const det = detections.find((d) => d.id === active);

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white grain">
      <header className="border-b border-[#27272A] bg-black/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="detection-logo-home"><img src="/assets/logo-dark-sm.png" alt="D2R" className="h-9 w-auto" /></Link>
          <div className="flex items-center gap-6">
            <Link to="/comment-s-inscrire" data-testid="detection-howto-link" className="hidden sm:flex items-center gap-2 text-xs text-[#C5A059] hover:text-[#E5C07B] transition-colors uppercase tracking-[0.2em]">
              <PlayCircle size={15} /> {t("det.howto")}
            </Link>
            <Link to="/evenement" data-testid="detection-event-link" className="text-xs text-[#A1A1AA] hover:text-[#C5A059] transition-colors uppercase tracking-[0.2em]">
              {t("event.cta2")}
            </Link>
            <Link to="/" data-testid="detection-back-link" className="flex items-center gap-2 text-xs text-[#A1A1AA] hover:text-[#C5A059] transition-colors uppercase tracking-[0.2em]">
              <ArrowLeft size={14} /> {t("eventpage.back")}
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/* Présentation de la détection D2R */}
        <section data-testid="detection-intro" className="py-24 lg:py-32 bg-[#0A0A0C] relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="text-[#C5A059] text-xs uppercase tracking-[0.4em] mb-6">{t("det.intro.eyebrow")}</p>
              <h1 className="font-display text-4xl md:text-6xl uppercase font-bold tracking-tight leading-[1.02] max-w-4xl">
                {t("det.intro.title.a")} <span className="text-[#C5A059]">{t("det.intro.title.b")}</span>
              </h1>
              <p className="text-lg leading-relaxed text-[#A1A1AA] mt-8 max-w-3xl">
                {t("det.intro.text")}
              </p>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#27272A] mt-16">
              {[t("det.intro.c1"), t("det.intro.c2"), t("det.intro.c3"), t("det.intro.c4")].map((c, i) => (
                <motion.div
                  key={c}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.15 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  className="bg-[#0A0A0C] p-8"
                >
                  <span className="font-editorial italic text-[#C5A059]">0{i + 1}</span>
                  <p className="text-sm text-[#A1A1AA] leading-relaxed mt-3">{c}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Onglets des détections */}
        {detections.length > 0 && (
          <div className="border-t border-[#27272A] bg-[#121214]">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
              <p className="text-[10px] uppercase tracking-[0.35em] text-[#71717A] pt-10">{t("det.tabs.label")}</p>
              <div className="flex gap-2 mt-4 overflow-x-auto pb-0" data-testid="detection-tabs">
                {detections.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => { setActive(d.id); }}
                    data-testid={`detection-tab-${d.id}`}
                    className={`flex items-center gap-3 px-6 py-4 text-xs uppercase tracking-[0.2em] whitespace-nowrap border-b-2 transition-colors duration-300 ${
                      active === d.id ? "border-[#C5A059] text-[#C5A059]" : "border-transparent text-[#71717A] hover:text-white"
                    }`}
                  >
                    {d.nom}
                    <span className={`text-[9px] px-2 py-0.5 border ${d.inscriptions_ouvertes ? "border-[#C5A059]/60 text-[#C5A059]" : "border-[#3f3f46] text-[#71717A]"}`}>
                      {d.inscriptions_ouvertes ? t("det.badge.open") : t("det.badge.closed")}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {det && (
          <div key={det.id}>
            <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 flex flex-wrap items-center gap-x-10 gap-y-4" data-testid="detection-active-info">
              <h2 className="font-display text-3xl md:text-4xl uppercase font-bold">{det.nom}</h2>
              <div className="flex flex-wrap gap-6 text-sm text-[#A1A1AA]">
                {det.dates && <span className="flex items-center gap-2"><CalendarDays size={15} className="text-[#C5A059]" /> {det.dates}</span>}
                {det.lieu && <span className="flex items-center gap-2"><MapPin size={15} className="text-[#C5A059]" /> {det.lieu}</span>}
              </div>
              {det.description && <p className="w-full text-[#71717A] text-sm leading-relaxed max-w-3xl">{det.description}</p>}
              <div className="flex flex-wrap gap-4">
                <Link to="/comment-s-inscrire" data-testid="detection-howto-button"
                  className="flex items-center gap-3 border border-[#C5A059] text-[#C5A059] px-6 py-3.5 text-xs uppercase tracking-[0.2em] hover:bg-[#C5A059] hover:text-black transition-colors duration-300">
                  <PlayCircle size={16} /> {t("det.howto")}
                </Link>
                <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="detection-whatsapp-button"
                  className="flex items-center gap-3 bg-[#25D366] text-black px-6 py-3.5 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#3be07a] transition-colors duration-300">
                  <MessageCircle size={16} /> {t("det.whatsapp")}
                </a>
                <Link to="/rendez-vous" data-testid="detection-rdv-libre"
                  className="flex items-center gap-3 border border-[#27272A] text-[#A1A1AA] px-6 py-3.5 text-xs uppercase tracking-[0.2em] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300">
                  <CalendarPlus size={16} /> {t("det.rdv.libre")}
                </Link>
              </div>
            </div>

            {det.affiche && (
              <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-10" data-testid="detection-affiche">
                <motion.img
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                  src={det.affiche}
                  alt={`Affiche — ${det.nom}`}
                  className="w-full max-w-xl mx-auto border border-[#27272A] shadow-2xl"
                />
              </div>
            )}

            {det.images && det.images.length > 0 && (
              <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-8" data-testid="detection-gallery">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {det.images.map((img, i) => (
                    <motion.img
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.06 }}
                      src={img}
                      alt={`${det.nom} — visuel ${i + 1}`}
                      loading="lazy"
                      className="w-full h-40 md:h-48 object-cover border border-[#27272A] hover:border-[#C5A059]/50 transition-colors duration-300"
                    />
                  ))}
                </div>
              </div>
            )}

            <Registration detection={det} />

            {det.programme && det.programme.length > 0 && (
              <section data-testid="detection-programme" className="py-28 lg:py-36 bg-[#0A0A0C] border-t border-[#27272A]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <p className="text-[#C5A059] text-xs uppercase tracking-[0.4em] mb-6">{det.lieu || t("event.meta.location")} — {det.dates}</p>
                    <h2 className="font-display text-4xl md:text-5xl uppercase font-bold tracking-tight leading-tight">
                      {t("eventpage.programme.full.a")} <span className="text-[#C5A059]">{t("eventpage.programme.full.b")}</span>
                    </h2>
                  </motion.div>
                  <BlurSoon testid="detection-programme-blur" active={!detailsVisibles}>
                    <motion.div
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                      className="mt-16 grid md:grid-cols-2 gap-x-14"
                    >
                      {det.programme.map((p, i) => (
                        <div key={i} className="flex gap-6 border-t border-[#27272A] py-7" data-testid={`detection-programme-item-${i}`}>
                          <p className="font-display text-[#C5A059] text-sm uppercase font-bold tracking-wider w-28 shrink-0 pt-1">{p.heure}</p>
                          <p className="text-base text-white leading-relaxed">{p.titre}</p>
                        </div>
                      ))}
                    </motion.div>
                  </BlurSoon>
                </div>
              </section>
            )}

            {det.id === "kinshasa-2026" && <ProgrammeOfficiel />}

            <Teams detectionId={det.id} />
            <Leaderboard detectionId={det.id} />
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
